import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { searchPatch, optionalSearch } from "@/lib/search";
import { SearchField } from "@/components/app/patterns/search-field";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RowActions } from "@/components/app/patterns/row-actions";
import { SharedRowNote } from "@/components/app/lists/shared-row-note";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Badge } from "@/components/ui/badge";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useNewRowPlace } from "@/hooks/use-new-row-place";
import { useCan, usePolicy } from "@/lib/policy";
import {
  DocumentTypeDialog,
  type DocumentTypeDraft,
} from "./-components/forms/document-type-dialog";
import { NoMatches } from "@/routes/_authenticated/_administration/settings/-components/no-matches";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";
import { guardRoute } from "@/lib/policy/route-guard";

export const Route = createFileRoute("/_authenticated/_administration/settings/document-types")({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: optionalSearch(search.q),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "document-types"),
  component: DocumentTypesSettings,
});

const fail = (error: Error) => toast.error(errorWords(error));
const STANDARD = "A standard type every operator shares, so only the platform team can change it.";

const NEW: DocumentTypeDraft = {
  id: null,
  name: "",
  isMedicalRecord: false,
  includeInFaceSheet: false,
  facilityId: null,
};

/** What a filed document can be called, and where it shows up. */
function DocumentTypesSettings() {
  const { facilityId } = useFacility();
  /* As the API curates the list: a building's type is changed with settings:manage there, one
     for every building with it for the company, a standard one by the platform team only, and a
     new one for every building also takes Administrator actions (`assertCurator`). */
  const { can } = usePolicy();
  const keepsCompanySettings = useCan("settings:manage", null);
  const administers = useCan("organization:administer", null);
  const curatesShared = keepsCompanySettings && administers;
  const buildings = useNewRowPlace("settings:manage");
  const place = { ...buildings, everyBuilding: curatesShared };
  const startAt =
    buildings.startAt ?? (curatesShared ? null : (buildings.buildings[0]?.id ?? null));
  const canManage = curatesShared || buildings.buildings.length > 0;
  const changeable = (type: { isSystemType: boolean; facilityId: string | null }) =>
    type.isSystemType ? can("platform:manage", null) : can("settings:manage", type.facilityId);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<DocumentTypeDraft | null>(null);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const { q } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const { text: search, change: setSearch } = useSearchParamText(q, (next) => patch({ q: next }));

  const types = useQuery(
    orpc.administration.catalogs.documentTypes.list.queryOptions({ input: { facilityId } }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.administration.catalogs.documentTypes.key() });

  const update = useMutation(
    orpc.administration.catalogs.documentTypes.update.mutationOptions({
      onSuccess: invalidate,
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.administration.catalogs.documentTypes.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Document type removed");
        setRemoving(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const needle = search.trim().toLowerCase();
  const rows = (types.data ?? []).filter(
    (type) => !needle || type.name.toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHeader
        title="Document types"
        meta="What staff can call a document when they file it, and which ones count as medical records or go on the face sheet."
      />

      <SearchField value={search} onChange={setSearch} placeholder="Search document types" />

      <RecordCard
        title="Types"
        count={types.data ? rows.length : undefined}
        canWrite={canManage}
        addLabel="Add document type"
        onAdd={() => setEditing({ ...NEW, facilityId: startAt })}
      >
        {types.isPending ? (
          <RowsSkeleton />
        ) : types.isError ? (
          <ReadFailed
            message="The document types could not be loaded."
            error={types.error}
            retrying={types.isFetching}
            onRetry={() => void types.refetch()}
          />
        ) : rows.length === 0 ? (
          needle ? (
            <NoMatches search={search} onClear={() => setSearch("")} />
          ) : (
            <EmptyRows text="No document types yet. Add one so staff can file papers under it, like Advance directive." />
          )
        ) : (
          <ul className="divide-y">
            {rows.map((type) => (
              <RecordRow
                key={type.id}
                title={
                  <span className="flex flex-wrap items-center gap-2">
                    {type.name}
                    {type.isSystemType ? <Badge variant="outline">Standard</Badge> : null}
                    {type.isMedicalRecord ? <Badge variant="secondary">Medical</Badge> : null}
                    {type.includeInFaceSheet ? <Badge variant="secondary">Face sheet</Badge> : null}
                  </span>
                }
                meta={type.facilityId ? "This building" : "Every building"}
                actions={
                  changeable(type) ? (
                    <RowActions
                      modal={false}
                      label={type.name}
                      primary={{
                        label: "Edit",
                        onSelect: () =>
                          setEditing({
                            id: type.id,
                            name: type.name,
                            isMedicalRecord: type.isMedicalRecord,
                            includeInFaceSheet: type.includeInFaceSheet,
                            facilityId: type.facilityId,
                          }),
                      }}
                      items={[
                        {
                          label: type.isMedicalRecord ? "Mark as not medical" : "Mark as medical",
                          disabled: update.isPending,
                          onSelect: () =>
                            update.mutate({
                              documentTypeId: type.id,
                              isMedicalRecord: !type.isMedicalRecord,
                            }),
                        },
                        !type.isSystemType && {
                          label: "Remove document type",
                          destructive: true,
                          onSelect: () => setRemoving({ id: type.id, name: type.name }),
                        },
                      ]}
                    />
                  ) : canManage && !type.facilityId ? (
                    <SharedRowNote reason={type.isSystemType ? STANDARD : undefined} />
                  ) : null
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      {editing ? (
        <DocumentTypeDialog
          key={editing.id ?? "new"}
          draft={editing}
          place={place}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {removing ? (
        <RemoveConfirm
          title={`Remove “${removing.name}”?`}
          confirmLabel="Remove document type"
          description="Documents already filed under it keep their label. It stops being offered when something new is filed."
          onConfirm={() => remove.mutate({ documentTypeId: removing.id })}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}
